import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, ShoppingCart, Package, CreditCard, Truck, Loader2, ExternalLink } from 'lucide-react';

// Check if a string looks like a size (enhanced with dimension patterns)
const looksLikeSize = (str) => {
  if (!str) return false;
  const s = str.trim();
  const sUpper = s.toUpperCase();
  // Common size patterns
  const sizePatterns = [
    /^(XXS|XS|S|M|L|XL|XXL|XXXL|2XL|3XL|4XL|5XL|6XL)$/i,
    /^(X-SMALL|SMALL|MEDIUM|LARGE|X-LARGE|XX-LARGE)$/i,
    /^(EXTRA SMALL|EXTRA LARGE)$/i,
    /^(ONE SIZE|OS|OSFA|FREE SIZE)$/i,
    /^\d+$/, // Numeric sizes like "32", "34"
    /^\d+["']?\s*[xX×]\s*\d+["']?$/, // Dimensions like "15x15", "18"x18", "24 x 36"
    /^\d+[\/x]\d+$/, // Sizes like "32/34" or "8x10"
    /^\d+\s*(OZ|INCH|IN|CM|MM|ML)$/i, // Sizes with units
  ];
  return sizePatterns.some(pattern => pattern.test(s) || pattern.test(sUpper));
};

// Check if a string looks like a color
const looksLikeColor = (str) => {
  if (!str) return false;
  const colorKeywords = [
    'black', 'white', 'red', 'blue', 'green', 'yellow', 'orange', 'purple', 'pink',
    'grey', 'gray', 'navy', 'brown', 'tan', 'beige', 'cream', 'gold', 'silver',
    'teal', 'maroon', 'burgundy', 'olive', 'coral', 'salmon', 'mint', 'lavender',
    'heather', 'charcoal', 'slate', 'forest', 'royal', 'sky', 'light', 'dark',
    'sport', 'athletic', 'vintage', 'ash', 'natural', 'sand', 'stone', 'ice',
    'midnight', 'cardinal', 'kelly', 'irish', 'carolina', 'military', 'hunter'
  ];
  const lower = str.toLowerCase();
  return colorKeywords.some(kw => lower.includes(kw));
};

// Detect label for a set of values by analyzing ALL values (majority vote)
const detectOptionLabel = (values) => {
  let colorScore = 0;
  let sizeScore = 0;

  values.forEach(v => {
    if (looksLikeColor(v)) colorScore++;
    if (looksLikeSize(v)) sizeScore++;
  });

  // Use majority to determine type
  if (colorScore > sizeScore && colorScore >= values.length * 0.4) return 'Color';
  if (sizeScore > colorScore && sizeScore >= values.length * 0.4) return 'Size';
  // If neither is dominant, check individual values
  if (colorScore > 0 && sizeScore === 0) return 'Color';
  if (sizeScore > 0 && colorScore === 0) return 'Size';
  return 'Style'; // Generic fallback
};

// Sort option values intelligently based on type
const sortOptionValues = (values, label) => {
  if (label === 'Size') {
    const sizeOrder = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL', '5XL'];
    return [...values].sort((a, b) => {
      const aIndex = sizeOrder.findIndex(s => a.toUpperCase().includes(s));
      const bIndex = sizeOrder.findIndex(s => b.toUpperCase().includes(s));
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      // Try numeric/dimension sorting for things like "15x15", "18x18"
      const aNum = parseFloat(a.replace(/[^\d.]/g, ''));
      const bNum = parseFloat(b.replace(/[^\d.]/g, ''));
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
      return a.localeCompare(b);
    });
  }
  // For colors and other options, keep original order or alphabetize
  return [...values].sort((a, b) => a.localeCompare(b));
};

// Analyze variants to determine option structure dynamically
// Instead of guessing per-variant, analyze ALL values at each position
const analyzeVariantOptions = (variants) => {
  if (!variants || variants.length === 0) return { options: [], variantMap: {} };

  const positionValues = [new Set(), new Set()]; // Max 2 option positions
  const variantMap = {};

  // Collect all unique values at each position
  variants.forEach(variant => {
    if (!variant.title) return;
    const parts = variant.title.split('/').map(p => p.trim());

    parts.forEach((part, idx) => {
      if (idx < 2 && part) positionValues[idx].add(part);
    });

    // Key by original order for accurate lookup
    const key = parts.join('|');
    variantMap[key] = variant;
  });

  // Determine label for each position by analyzing ALL its values
  const options = positionValues.map((valuesSet, idx) => {
    const values = Array.from(valuesSet);
    if (values.length === 0) return null;

    const label = detectOptionLabel(values);
    const sortedValues = sortOptionValues(values, label);
    const isColor = label === 'Color';

    // For color options, also compute hex values
    const valuesWithMeta = isColor
      ? sortedValues.map(v => ({ name: v, hex: getColorHex(v) }))
      : sortedValues;

    return {
      label,
      values: valuesWithMeta,
      isColor,
      position: idx
    };
  }).filter(Boolean);

  return { options, variantMap };
};

// Map common color names to hex values with fuzzy matching
const getColorHex = (colorName) => {
  const lowerName = colorName.toLowerCase().trim();

  // Exact matches first
  const exactMap = {
    'black': '#000000',
    'white': '#FFFFFF',
    'navy': '#1e3a5f',
    'navy blue': '#1e3a5f',
    'red': '#DC2626',
    'blue': '#2563EB',
    'royal blue': '#1D4ED8',
    'royal': '#1D4ED8',
    'green': '#16A34A',
    'forest green': '#166534',
    'forest': '#166534',
    'gray': '#6B7280',
    'grey': '#6B7280',
    'heather gray': '#9CA3AF',
    'heather grey': '#9CA3AF',
    'dark heather': '#4B5563',
    'charcoal': '#374151',
    'pink': '#EC4899',
    'light pink': '#F9A8D4',
    'purple': '#9333EA',
    'yellow': '#EAB308',
    'orange': '#EA580C',
    'brown': '#92400E',
    'tan': '#D4A574',
    'khaki': '#C3B091',
    'beige': '#F5F5DC',
    'cream': '#FFFDD0',
    'maroon': '#7F1D1D',
    'burgundy': '#800020',
    'cardinal': '#C41E3A',
    'teal': '#0D9488',
    'turquoise': '#06B6D4',
    'light blue': '#7DD3FC',
    'sky blue': '#38BDF8',
    'carolina blue': '#56A0D3',
    'olive': '#6B8E23',
    'military green': '#4B5320',
    'silver': '#C0C0C0',
    'gold': '#D4AF37',
    'natural': '#F5F5DC',
    'sand': '#C2B280',
    'athletic heather': '#D1D5DB',
    'sport grey': '#9CA3AF',
    'ash': '#B2BEB5',
    'heather': '#9CA3AF',
    'irish green': '#009A44',
    'kelly green': '#4CBB17',
    'lime': '#32CD32',
    'sapphire': '#0F52BA',
    'indigo': '#4B0082',
    'violet': '#8B5CF6',
    'orchid': '#DA70D6',
    'coral': '#FF7F50',
    'salmon': '#FA8072',
    'peach': '#FFCBA4',
    'mint': '#98FB98',
    'aqua': '#00FFFF',
    'cyan': '#00FFFF',
    'slate': '#708090',
    'stone': '#928E85',
    'taupe': '#483C32',
    'chocolate': '#7B3F00',
    'espresso': '#4E312D',
    'coffee': '#6F4E37',
    'rust': '#B7410E',
    'copper': '#B87333',
    'bronze': '#CD7F32',
    'wine': '#722F37',
    'plum': '#8E4585',
    'lavender': '#E6E6FA',
    'lilac': '#C8A2C8',
    'mauve': '#E0B0FF',
    'fuchsia': '#FF00FF',
    'magenta': '#FF00FF',
    'hot pink': '#FF69B4',
    'raspberry': '#E30B5C',
    'cranberry': '#9E003A',
    'cherry': '#DE3163',
    'scarlet': '#FF2400',
    'crimson': '#DC143C',
    'brick': '#CB4154',
    'terracotta': '#E2725B',
    'burnt orange': '#CC5500',
    'pumpkin': '#FF7518',
    'tangerine': '#FF9966',
    'apricot': '#FBCEB1',
    'mustard': '#FFDB58',
    'sunflower': '#FFDA03',
    'lemon': '#FFF44F',
    'banana': '#FFE135',
    'butter': '#FFFF99',
    'canary': '#FFEF00',
    'chartreuse': '#7FFF00',
    'grass': '#7CFC00',
    'emerald': '#50C878',
    'jade': '#00A86B',
    'hunter': '#355E3B',
    'pine': '#01796F',
    'spruce': '#2E8B57',
    'sage': '#9DC183',
    'seafoam': '#9FE2BF',
    'ocean': '#1A5276',
    'denim': '#1560BD',
    'cobalt': '#0047AB',
    'pacific': '#1CA9C9',
    'ice': '#99FFFF',
    'powder': '#B0E0E6',
    'periwinkle': '#CCCCFF',
    'steel': '#71797E',
    'graphite': '#383838',
    'charcoal heather': '#4B5563',
    'dark grey': '#4B5563',
    'dark gray': '#4B5563',
    'light grey': '#D1D5DB',
    'light gray': '#D1D5DB',
    'midnight': '#191970',
    'midnight navy': '#191970',
    'heather midnight navy': '#2C3E50',
  };

  if (exactMap[lowerName]) {
    return exactMap[lowerName];
  }

  // Fuzzy matching - check if color name contains key words
  const fuzzyRules = [
    { keywords: ['black'], hex: '#000000' },
    { keywords: ['white', 'snow', 'ivory'], hex: '#FFFFFF' },
    { keywords: ['navy', 'midnight'], hex: '#1e3a5f' },
    { keywords: ['red', 'cherry', 'crimson', 'scarlet'], hex: '#DC2626' },
    { keywords: ['blue', 'sapphire', 'cobalt', 'denim'], hex: '#2563EB' },
    { keywords: ['royal'], hex: '#1D4ED8' },
    { keywords: ['green', 'forest', 'hunter', 'pine'], hex: '#166534' },
    { keywords: ['kelly', 'irish', 'lime', 'grass'], hex: '#22C55E' },
    { keywords: ['grey', 'gray', 'charcoal', 'slate', 'graphite'], hex: '#6B7280' },
    { keywords: ['heather'], hex: '#9CA3AF' },
    { keywords: ['pink', 'rose', 'blush'], hex: '#EC4899' },
    { keywords: ['purple', 'violet', 'plum', 'grape'], hex: '#9333EA' },
    { keywords: ['yellow', 'gold', 'mustard', 'lemon'], hex: '#EAB308' },
    { keywords: ['orange', 'tangerine', 'rust', 'burnt'], hex: '#EA580C' },
    { keywords: ['brown', 'chocolate', 'coffee', 'espresso'], hex: '#92400E' },
    { keywords: ['tan', 'khaki', 'sand', 'beige', 'cream', 'natural'], hex: '#D4A574' },
    { keywords: ['maroon', 'burgundy', 'wine'], hex: '#7F1D1D' },
    { keywords: ['teal', 'turquoise', 'aqua', 'cyan'], hex: '#0D9488' },
    { keywords: ['olive', 'military', 'army'], hex: '#6B8E23' },
    { keywords: ['silver', 'ash'], hex: '#C0C0C0' },
    { keywords: ['cardinal'], hex: '#C41E3A' },
    { keywords: ['carolina'], hex: '#56A0D3' },
    { keywords: ['sport'], hex: '#9CA3AF' },
    { keywords: ['athletic'], hex: '#D1D5DB' },
    { keywords: ['dark'], hex: '#4B5563' },
    { keywords: ['light'], hex: '#D1D5DB' },
  ];

  for (const rule of fuzzyRules) {
    if (rule.keywords.some(kw => lowerName.includes(kw))) {
      return rule.hex;
    }
  }

  // Default gray if nothing matches
  return '#9CA3AF';
};

const CheckoutModal = ({ product, isOpen, onClose, onOrderComplete, api, initialColor, initialImage }) => {
  const [step, setStep] = useState(0); // 0: publishing check, 1: variant selection, 2: customer info, 3: redirecting to stripe
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]); // Array of selected values by position index
  const [displayImage, setDisplayImage] = useState(null); // Current mockup image to display
  const [quantity, setQuantity] = useState(1);
  const [customerInfo, setCustomerInfo] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [shippingCost, setShippingCost] = useState(null);
  const [isLoadingShipping, setIsLoadingShipping] = useState(false);

  // Parse variants into dynamic options (Color, Size, Style, etc.)
  const { options, variantMap } = useMemo(() => {
    const result = analyzeVariantOptions(product?.variants);
    console.log('CheckoutModal - Product variants:', product?.variants);
    console.log('CheckoutModal - Analyzed options:', result.options);
    console.log('CheckoutModal - Variant map keys:', Object.keys(result.variantMap));
    return result;
  }, [product?.variants]);

  // Find matching variant when options change
  useEffect(() => {
    if (!options.length) {
      // No options - select first enabled variant
      const firstEnabled = product?.variants?.find(v => v.is_enabled);
      setSelectedVariant(firstEnabled || null);
      return;
    }

    // Build lookup key from selected options in order
    const key = selectedOptions.join('|');
    const matchedVariant = variantMap[key];
    setSelectedVariant(matchedVariant || null);
  }, [selectedOptions, variantMap, options.length, product?.variants]);

  // Go directly to variant selection when modal opens
  useEffect(() => {
    if (isOpen && product) {
      setStep(1); // Go directly to variant selection
      setError(null);
      setShippingCost(null);

      // Set initial display image if provided, otherwise use product default
      setDisplayImage(initialImage || product.mainImage || product.mockupUrl);

      // Pre-select initial color if provided
      if (initialColor && options.length > 0) {
        const newSelectedOptions = [];
        options.forEach((option, idx) => {
          if (option.isColor) {
            // Find matching color in this option's values
            const matchingColor = option.values.find(v =>
              v.name?.toLowerCase() === initialColor.toLowerCase()
            );
            if (matchingColor) {
              newSelectedOptions[idx] = matchingColor.name;
            }
          }
        });
        setSelectedOptions(newSelectedOptions);
      } else {
        setSelectedOptions([]); // Reset selected options
      }
      setSelectedVariant(null);
    }
  }, [isOpen, product, initialColor, initialImage, options]);

  // Fetch shipping cost when address is complete enough
  const fetchShippingCost = useCallback(async () => {
    if (!selectedVariant || !customerInfo.country || !customerInfo.zip) {
      setShippingCost(null);
      return;
    }

    setIsLoadingShipping(true);
    try {
      const response = await api.getShippingEstimate(
        product.printifyProductId || product.id,
        selectedVariant.id,
        customerInfo.country,
        customerInfo.zip
      );

      if (response.success) {
        setShippingCost(response.shippingCost);
      } else {
        console.log('Could not get shipping estimate:', response.error);
        setShippingCost(null);
      }
    } catch (err) {
      console.error('Error fetching shipping cost:', err);
      setShippingCost(null);
    } finally {
      setIsLoadingShipping(false);
    }
  }, [api, product, selectedVariant, customerInfo.country, customerInfo.zip]);

  // Debounced shipping fetch when address changes
  useEffect(() => {
    if (step === 2 && customerInfo.country && customerInfo.zip && customerInfo.zip.length >= 3) {
      const timer = setTimeout(() => {
        fetchShippingCost();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [step, customerInfo.country, customerInfo.zip, fetchShippingCost]);

  if (!isOpen || !product) return null;

  // Handle option selection at a specific position
  const handleOptionSelect = (position, value, isColorOption = false) => {
    setSelectedOptions(prev => {
      const newOptions = [...prev];
      // Ensure array is long enough
      while (newOptions.length <= position) {
        newOptions.push(null);
      }
      newOptions[position] = value;
      return newOptions;
    });

    // Update display image when color is selected
    if (isColorOption && product?.images?.length > 0 && product?.variants?.length > 0) {
      // Find variant IDs that match this color name
      const matchingVariantIds = product.variants
        .filter(v => v.title?.toLowerCase().includes(value.toLowerCase()))
        .map(v => v.id);

      // Find mockup image that includes any of these variant IDs
      const matchingImage = product.images.find(img =>
        img.variant_ids?.some(vid => matchingVariantIds.includes(vid))
      );

      if (matchingImage?.src) {
        setDisplayImage(matchingImage.src);
      }
    }
  };

  const handleCustomerInfoChange = (field, value) => {
    setCustomerInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateCustomerInfo = () => {
    const required = ['first_name', 'last_name', 'email', 'address1', 'city', 'zip', 'country'];
    const missing = required.filter(field => !customerInfo[field]?.trim());

    if (missing.length > 0) {
      setError(`Please fill in: ${missing.join(', ').replace(/_/g, ' ')}`);
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerInfo.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSubmitOrder = async () => {
    if (!validateCustomerInfo()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Create Stripe checkout session and redirect
      const response = await api.createCheckoutSession({
        productId: product.printifyProductId || product.id,
        variantId: selectedVariant.id,
        quantity,
        customerEmail: customerInfo.email,
        shippingAddress: {
          first_name: customerInfo.first_name,
          last_name: customerInfo.last_name,
          phone: customerInfo.phone,
          address1: customerInfo.address1,
          address2: customerInfo.address2,
          city: customerInfo.city,
          state: customerInfo.state,
          zip: customerInfo.zip,
          country: customerInfo.country
        }
      });

      if (response.success && response.checkoutUrl) {
        setStep(3); // Show redirecting state
        // Redirect to Stripe checkout
        window.location.href = response.checkoutUrl;
      } else {
        setError(response.error || 'Failed to create checkout session');
        setIsSubmitting(false);
      }
    } catch (err) {
      setError('Failed to initiate checkout. Please try again.');
      console.error('Checkout error:', err);
      setIsSubmitting(false);
    }
  };

  const resetModal = () => {
    setStep(0);
    setSelectedVariant(null);
    setSelectedOptions([]);
    setQuantity(1);
    setCustomerInfo({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US'
    });
    setError(null);
    setIsSubmitting(false);
    setShippingCost(null);
    setIsLoadingShipping(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const subtotal = selectedVariant ? (selectedVariant.price * quantity) : 0;
  const shippingDisplay = shippingCost !== null ? (shippingCost / 100) : null;
  const total = selectedVariant
    ? (subtotal + (shippingDisplay || 0)).toFixed(2)
    : '0.00';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in" onClick={handleClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-blue-600" />
            Checkout
          </h2>
          <button
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-2 rounded-full transition-colors"
            onClick={handleClose}
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 max-h-[80vh] overflow-y-auto">
          {/* Product Summary */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8 p-4 bg-slate-50 rounded-xl border border-slate-100">
            <img
              src={displayImage || product.mainImage || product.mockupUrl}
              alt={product.title}
              className="w-full sm:w-48 h-48 object-cover rounded-lg bg-white border border-slate-200 flex-shrink-0"
            />
            <div className="flex flex-col justify-center">
              <h3 className="font-semibold text-slate-900 text-lg">{product.title}</h3>
              <p className="text-sm text-slate-500 mt-2 line-clamp-4">{product.description}</p>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 mb-6">
              <span>⚠️</span> {error}
            </div>
          )}

          {/* Step 1: Variant Selection */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-lg font-semibold text-slate-900 pb-2 border-b border-slate-100">
                <Package className="w-5 h-5 text-blue-600" />
                Select Options
              </div>

              {/* Dynamic Option Sections */}
              {options.map((option, optionIndex) => (
                <div key={optionIndex} className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    {option.label}
                    {selectedOptions[optionIndex] && (
                      <span className="text-slate-500"> — {selectedOptions[optionIndex]}</span>
                    )}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {option.isColor ? (
                      // Render color swatches
                      option.values.map((color) => {
                        const isSelected = selectedOptions[optionIndex] === color.name;
                        const isWhite = color.hex.toLowerCase() === '#ffffff' || color.hex.toLowerCase() === '#fff';

                        return (
                          <button
                            key={color.name}
                            onClick={() => handleOptionSelect(optionIndex, color.name, true)}
                            className={`
                              group relative w-10 h-10 rounded-full transition-all duration-200
                              ${isSelected
                                ? 'ring-2 ring-blue-500 ring-offset-2 scale-110'
                                : 'hover:scale-110 hover:ring-2 hover:ring-slate-300 hover:ring-offset-1'
                              }
                            `}
                            title={color.name}
                          >
                            <span
                              className={`block w-full h-full rounded-full shadow-sm ${isWhite ? 'border border-slate-200' : ''}`}
                              style={{ backgroundColor: color.hex }}
                            />
                            {/* Tooltip */}
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                              {color.name}
                            </span>
                          </button>
                        );
                      })
                    ) : (
                      // Render text buttons for Size, Style, etc.
                      option.values.map((value) => {
                        const isSelected = selectedOptions[optionIndex] === value;

                        return (
                          <button
                            key={value}
                            onClick={() => handleOptionSelect(optionIndex, value)}
                            className={`
                              px-4 py-2 border rounded-lg font-medium text-sm transition-all
                              ${isSelected
                                ? 'border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-500'
                                : 'border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                              }
                            `}
                          >
                            {value}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}

              {/* Quantity */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-slate-700">Quantity</label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600 font-bold"
                  >
                    −
                  </button>
                  <span className="w-12 text-center font-semibold text-lg">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(10, quantity + 1))}
                    className="w-10 h-10 flex items-center justify-center border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600 font-bold"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Price Display & Continue */}
              <div className={`mt-6 flex items-center justify-between p-4 rounded-xl transition-all ${selectedVariant ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-400'}`}>
                <div className="flex flex-col">
                  <span className={`text-sm ${selectedVariant ? 'text-slate-400' : 'text-slate-500'}`}>
                    {selectedVariant ? 'Total Amount' : 'Select options above'}
                  </span>
                  {selectedVariant ? (
                    <>
                      <span className="text-2xl font-bold">${total}</span>
                      <span className="text-xs text-slate-400">
                        {quantity}x ${selectedVariant.price} each
                      </span>
                    </>
                  ) : (
                    <span className="text-lg font-semibold">
                      {options.length > 0
                        ? `Choose ${options.find((opt, idx) => !selectedOptions[idx])?.label.toLowerCase() || 'an option'}`
                        : 'No options available'}
                    </span>
                  )}
                </div>
                <button
                  disabled={!selectedVariant}
                  className={`px-6 py-2 rounded-lg font-bold transition-colors ${
                    selectedVariant
                      ? 'bg-white text-slate-900 hover:bg-blue-50'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                  onClick={() => setStep(2)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Customer Information */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-lg font-semibold text-slate-900 pb-2 border-b border-slate-100">
                <CreditCard className="w-5 h-5 text-blue-600" />
                Shipping Information
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input type="text" placeholder="First Name *" value={customerInfo.first_name} onChange={e => handleCustomerInfoChange('first_name', e.target.value)} className="input-field" />
                <input type="text" placeholder="Last Name *" value={customerInfo.last_name} onChange={e => handleCustomerInfoChange('last_name', e.target.value)} className="input-field" />
              </div>

              <input type="email" placeholder="Email Address *" value={customerInfo.email} onChange={e => handleCustomerInfoChange('email', e.target.value)} className="input-field" />
              <input type="tel" placeholder="Phone Number" value={customerInfo.phone} onChange={e => handleCustomerInfoChange('phone', e.target.value)} className="input-field" />

              <input type="text" placeholder="Street Address *" value={customerInfo.address1} onChange={e => handleCustomerInfoChange('address1', e.target.value)} className="input-field" />
              <input type="text" placeholder="Apartment, suite, etc." value={customerInfo.address2} onChange={e => handleCustomerInfoChange('address2', e.target.value)} className="input-field" />

              <div className="grid grid-cols-3 gap-4">
                <input type="text" placeholder="City *" value={customerInfo.city} onChange={e => handleCustomerInfoChange('city', e.target.value)} className="input-field" />
                <input type="text" placeholder="State" value={customerInfo.state} onChange={e => handleCustomerInfoChange('state', e.target.value)} className="input-field" />
                <input type="text" placeholder="ZIP *" value={customerInfo.zip} onChange={e => handleCustomerInfoChange('zip', e.target.value)} className="input-field" />
              </div>

              <select value={customerInfo.country} onChange={e => handleCustomerInfoChange('country', e.target.value)} className="input-field">
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="GB">United Kingdom</option>
                <option value="AU">Australia</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
                <option value="ES">Spain</option>
                <option value="IT">Italy</option>
                <option value="NL">Netherlands</option>
              </select>

              {/* Shipping Cost Display */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex items-center gap-2 text-slate-700">
                  <Truck className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">Shipping</span>
                </div>
                <div className="text-right">
                  {isLoadingShipping ? (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Calculating...</span>
                    </div>
                  ) : shippingDisplay !== null ? (
                    <span className="font-semibold text-slate-900">${shippingDisplay.toFixed(2)}</span>
                  ) : customerInfo.zip && customerInfo.country ? (
                    <span className="text-sm text-slate-500">Unable to calculate</span>
                  ) : (
                    <span className="text-sm text-slate-500">Enter ZIP to calculate</span>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between pt-6 border-t border-slate-100">
                <button
                  className="px-6 py-2 text-slate-600 hover:text-slate-900 font-medium"
                  onClick={() => setStep(1)}
                >
                  Back
                </button>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <span className="block text-xs text-slate-500">Subtotal: ${subtotal.toFixed(2)}</span>
                    <span className="block font-bold text-slate-900">Total: ${total}</span>
                  </div>
                  <button
                    className="btn-primary flex items-center gap-2"
                    onClick={handleSubmitOrder}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4" />
                        Pay with Stripe
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Redirecting to Stripe */}
          {step === 3 && (
            <div className="text-center py-12 space-y-6">
              <div className="w-20 h-20 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 size={40} className="animate-spin" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Redirecting to Payment...</h3>
                <p className="text-slate-600">You'll be taken to Stripe's secure checkout page.</p>
                <p className="text-slate-500 text-sm mt-2 flex items-center justify-center gap-1">
                  <ExternalLink className="w-4 h-4" />
                  Powered by Stripe
                </p>
              </div>

              <button
                className="text-slate-500 hover:text-slate-700 text-sm underline"
                onClick={handleClose}
              >
                Cancel and go back
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;